import { apiSlice } from '../apiSlice';

interface SearchDataProps {
  clientCode: string;
  bookName: string;
  author: string;
  isbn: string;
  publisherName: string;
  courseCode: number;
  bookLevel: string;
  pageNumber: number;
}

export const bookAPI = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['Book', 'Copy', 'Lending', 'Return', 'SMS'],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getBooksBySearchData: build.query({
        query: (searchData: SearchDataProps) => ({
          url: `/mgmt/books`,
          method: 'GET',
          params: searchData,
        }),
        providesTags: ['Book'],
      }),
      getExcelByBooks: build.query({
        query: ({ clientCode, ...params }) => ({
          url: `/mgmt/books/excel/${clientCode}`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['Book'],
      }),
      getBookInformationByBookNo: build.query({
        query: ({ bookNo, campusCode }: any) => ({
          url: `/mgmt/books/${campusCode}/${bookNo}`,
          method: 'GET',
        }),
        providesTags: ['Book'],
      }),
      getExcelUploadCount: build.query({
        query: (data: any) => ({
          url: `/mgmt/books/valid/excel`,
          method: 'POST',
          data: data,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      }),
      getExcelQuantityUploadCount: build.query({
        query: ({ data, campusCode }) => ({
          url: `/mgmt/books/valid/excel/${campusCode}`,
          method: 'POST',
          data: data,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
      }),
      getExcelBookTemplate: build.query({
        query: () => ({
          url: `/mgmt/books/excel/template`,
          method: 'GET',
          responseType: 'blob',
        }),
      }),
      getCampusExcelBookTemplate: build.query({
        query: ({ campusCode }) => ({
          url: `/mgmt/books/excel/template/${campusCode}`,
          method: 'GET',
          responseType: 'blob',
        }),
      }),
      deleteBook: build.mutation({
        query: ({ bookNo }: { bookNo: number }) => ({
          url: `/mgmt/books/${bookNo}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['Book'],
      }),
      excelBatchUpload: build.mutation({
        query: (data: any) => ({
          url: `/mgmt/books/excel`,
          method: 'POST',
          data: data,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
        invalidatesTags: ['Book'],
      }),
      excelCampusBookQuantityBatchUpload: build.mutation({
        query: (data: any) => ({
          url: `/mgmt/books/excel/${data.get('campusCode')}`,
          method: 'POST',
          data: data,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }),
        invalidatesTags: ['Book'],
      }),
      updateBasicBookInformation: build.mutation({
        query: (data: any) => ({
          url: `/mgmt/books/${data.bookNo}`,
          method: 'PUT',
          data,
        }),
        invalidatesTags: ['Book'],
      }),
      saveUnitBook: build.mutation({
        query: (data: any) => ({
          url: `/mgmt/books`,
          method: 'POST',
          data,
        }),
        invalidatesTags: ['Book'],
      }),
      getCopyBooks: build.query({
        query: ({ campusCode, ...searchData }) => ({
          url: `/mgmt/books/copy/${campusCode}`,
          method: 'GET',
          params: { ...searchData, campusCode },
        }),
        providesTags: ['Copy'],
      }),
      getReturnBooks: build.query({
        query: ({ campusCode, ...searchData }) => ({
          url: `/mgmt/books/return/${campusCode}`,
          method: 'GET',
          params: { ...searchData, campusCode },
        }),
        providesTags: ['Return'],
      }),
      getCopyBookInformation: build.query({
        query: ({ campusCode, bookNo, bookSeq }) => ({
          url: `/mgmt/books/copy/${campusCode}/${bookNo}`,
          method: 'GET',
          params: { bookSeq },
        }),
        providesTags: ['Copy'],
      }),
      getCopyBookSeqCheckList: build.query({
        query: ({ campusCode, bookNo }) => ({
          url: `/mgmt/books/copy/${campusCode}/${bookNo}/check`,
          method: 'GET',
        }),
        providesTags: ['Copy'],
      }),
      saveCopyBook: build.mutation({
        query: ({ campusCode, bookNo, bookSeq }) => ({
          url: `/mgmt/books/copy/${campusCode}/${bookNo}`,
          method: 'POST',
          data: {
            campusCode,
            bookNo,
            bookSeq,
          },
        }),
        invalidatesTags: ['Book', 'Copy'],
      }),
      deleteCopyBook: build.mutation({
        query: ({ clientBookNo, lendingCode }) => ({
          url: `/mgmt/books/copy/${clientBookNo}`,
          method: 'DELETE',
          params: {
            lendingCode,
          },
        }),
        invalidatesTags: ['Book', 'Copy'],
      }),
      getStudentByCampusClass: build.query({
        query: ({ campusCode, classCode, ...params }) => ({
          url: `/mgmt/books/${campusCode}/${classCode}/students`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['Lending'],
      }),
      getAvailableLoanableBooks: build.query({
        query: ({ campusCode, ...params }) => ({
          url: `/mgmt/books/${campusCode}/available`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['Lending'],
      }),
      saveBookLoan: build.mutation({
        query: (data) => ({
          url: `/mgmt/books/loan`,
          method: 'POST',
          data,
        }),
        invalidatesTags: ['Lending', 'Copy', 'Return'],
      }),
      getStudentBookLoanHistory: build.query({
        query: ({ campusCode, classCode, studentMemberCode, ...params }) => ({
          url: `/mgmt/books/${campusCode}/${classCode}/${studentMemberCode}/loan`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['Lending'],
      }),
      cancelBookLoan: build.mutation({
        query: (data) => ({
          url: `/mgmt/books/loans/cancel`,
          method: 'POST',
          data,
        }),
        invalidatesTags: ['Lending', 'Copy', 'Return'],
      }),
      getBookLoanDuplicate: build.query({
        query: (data) => ({
          url: `/mgmt/books/loans/check-duplicate`,
          method: 'POST',
          data,
        }),
        providesTags: ['Lending'],
      }),
      getCheckedOutBooks: build.query({
        query: ({ campusCode, ...params }) => ({
          url: `/mgmt/books/checked-out/${campusCode}`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['SMS', 'Lending'],
      }),
      saveOverdueSMS: build.mutation({
        query: (data) => ({
          url: `/mgmt/books/checked-out/send`,
          method: 'POST',
          data,
        }),
        invalidatesTags: ['SMS'],
      }),
      getStatisticsBooks: build.query({
        query: ({ semesterCode, ...params }) => ({
          url: `/mgmt/statistics/books/${semesterCode}`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['Lending', 'Copy'],
      }),
      getStudentHistoriesBooks: build.query({
        query: ({ classCode, studentCode, ...params }) => ({
          url: `/mgmt/books/${classCode}/${studentCode}/histories`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['Lending'],
      }),
      //
      getClass: build.query({
        query: ({ campusCode, ...params }) => ({
          url: `/mgmt/books/class/campus/${campusCode}/semester`,
          method: 'GET',
          params: params,
        }),
        providesTags: ['Return'],
      }),
      //
    }),
  });

export const {
  useLazyGetBooksBySearchDataQuery,
  useLazyGetExcelUploadCountQuery,
  useLazyGetExcelQuantityUploadCountQuery,
  useLazyGetExcelBookTemplateQuery,
  useLazyGetCampusExcelBookTemplateQuery,
  useGetBookInformationByBookNoQuery,
  useUpdateBasicBookInformationMutation,
  useSaveUnitBookMutation,
  useDeleteBookMutation,
  useExcelBatchUploadMutation,
  useExcelCampusBookQuantityBatchUploadMutation,
  useGetCopyBooksQuery,
  useGetReturnBooksQuery,
  useGetCopyBookInformationQuery,
  useGetCopyBookSeqCheckListQuery,
  useSaveCopyBookMutation,
  useDeleteCopyBookMutation,
  useLazyGetStudentByCampusClassQuery,
  useLazyGetAvailableLoanableBooksQuery,
  useSaveBookLoanMutation,
  useLazyGetStudentBookLoanHistoryQuery,
  useCancelBookLoanMutation,
  useLazyGetBookLoanDuplicateQuery,
  useGetCheckedOutBooksQuery,
  useLazyGetCheckedOutBooksQuery,
  useSaveOverdueSMSMutation,
  useLazyGetExcelByBooksQuery,
  useLazyGetStatisticsBooksQuery,
  useGetStudentHistoriesBooksQuery,
  useGetClassQuery,
}: any = bookAPI;
